import { useEffect, useState, lazy, Suspense } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMacro } from '@/hooks/useMacro'
import PositioningCard from '@/components/PositioningCard'
import {
  Globe, RefreshCw, TrendingUp, TrendingDown,
  AlertTriangle, CheckCircle, DollarSign, Activity, Calendar,
} from 'lucide-react'
import { clsx } from 'clsx'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, LineChart, Line, Legend, ReferenceLine,
} from 'recharts'
import type { CentralBankPolicy, EconomicRelease, UKEconomyDashboard, UKLeadingIndicatorsResponse, UKRatesResponse, GlobalRankingResponse, GroupSnapshotResponse, MacroVolatilityIndex, StreetSentimentResponse } from '@/lib/types'

// ── Helpers ─────────────────────────────────────────────────────────────────

function pct(n: number) {
  return `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`
}

function fmtTn(n: number) {
  return `$${n.toFixed(2)}T`
}

const SIGNAL_COLORS: Record<string, string> = {
  green: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-rose-500',
}

const SIGNAL_TEXT_COLORS: Record<string, string> = {
  green: 'text-emerald-400',
  amber: 'text-amber-400',
  red: 'text-rose-400',
}

const TAB_IDS = ['overview', 'rates', 'inflation', 'indicators', 'monetary', 'surprise', 'heatmap', 'rankings', 'uk', 'economic-index'] as const

// Lazy tab content re-homed from the former standalone /app/economic-index page
const EconomicIndexTab = lazy(() => import('./EconomicIndexPage'))

// ── Tab bar ─────────────────────────────────────────────────────────────────

function TabBar({ active, onChange }: { active: string; onChange: (t: string) => void }) {
  const tabs = [
    { id: 'overview',  label: 'Overview' },
    { id: 'rates',     label: 'Central Bank Rates' },
    { id: 'inflation', label: 'Inflation' },
    { id: 'indicators',label: 'Leading Indicators' },
    { id: 'monetary',  label: 'Monetary Policy' },
    { id: 'surprise',  label: 'Economic Surprise' },
    { id: 'heatmap',   label: 'Global Heatmap' },
    { id: 'rankings',  label: 'Global Rankings' },
    { id: 'uk',        label: '🇬🇧 UK Economy' },
    { id: 'economic-index', label: 'Economic Index' },
  ]
  return (
    // overflow-x-auto: 10 tabs overflow narrow viewports — scroll, don't clip (mobile parity)
    <div className="glass flex gap-1 rounded-xl p-1 overflow-x-auto sticky top-0 z-20">
      {tabs.map(t => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className={clsx(
            'px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap shrink-0',
            active === t.id
              ? 'bg-blue/20 text-blue-light'
              : 'text-text-secondary hover:text-text-primary hover:bg-surface-DEFAULT',
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  )
}

// ── Country Flag Grid ───────────────────────────────────────────────────────

function CountryGrid({
  data,
}: {
  data: Array<{ country: string; name: string; flag: string; gdp_growth: number; gdp_nominal_tn: number }>
}) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 stagger-children">
      {data.map(c => (
        <div
          key={c.country}
          className="card-hover p-4"
        >
          <div className="text-2xl mb-1">{c.flag}</div>
          <p className="text-sm font-medium text-text-primary">{c.name}</p>
          <p className="text-xs text-text-muted">{c.country}</p>
          <div className="mt-2 space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-text-muted">GDP Growth</span>
              <span className={c.gdp_growth >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
                {pct(c.gdp_growth)}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-text-muted">GDP</span>
              <span className="text-text-secondary">{fmtTn(c.gdp_nominal_tn)}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Yield Curve Chart ───────────────────────────────────────────────────────

function YieldCurveChart({ curves }: { curves: Record<string, Record<string, number | null>> }) {
  const tenors = ['2Y', '5Y', '10Y', '30Y']
  const colors: Record<string, string> = { US: '#3b82f6', GB: '#f59e0b', DE: '#22c55e' }

  const chartData = tenors.map(t => {
    const point: Record<string, string | number | null> = { tenor: t }
    for (const [country, curve] of Object.entries(curves)) {
      point[country] = curve?.[t] ?? null
    }
    return point
  })

  return (
    <div className="card p-4">
      <h3 className="panel-title mb-3">Yield Curves</h3>
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis dataKey="tenor" tick={{ fill: '#9ca3af', fontSize: 12 }} />
          <YAxis tick={{ fill: '#9ca3af', fontSize: 12 }} domain={['auto', 'auto']} unit="%" />
          <Tooltip
            contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: 8, color: 'rgb(var(--text-primary))'}}
            labelStyle={{ color: '#e5e7eb' }}
          />
          <Legend />
          {Object.entries(colors).map(([country, color]) => (
            <Line
              key={country}
              type="monotone"
              dataKey={country}
              stroke={color}
              strokeWidth={2}
              dot={{ fill: color, r: 4 }}
              name={country}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

// ── Central Bank Rates Bar Chart ────────────────────────────────────────────

function RatesBarChart({
  rates,
}: {
  rates: Array<{ country: string; name: string; rate: number; flag: string }>
}) {
  const barColors = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4']

  return (
    <div className="card p-4">
      <h3 className="panel-title mb-3">Central Bank Rates</h3>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={rates} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis type="number" tick={{ fill: '#9ca3af', fontSize: 12 }} unit="%" />
          <YAxis
            dataKey="country"
            type="category"
            tick={{ fill: '#9ca3af', fontSize: 12 }}
            width={40}
          />
          <Tooltip
            contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: 8, color: 'rgb(var(--text-primary))'}}
            formatter={(val: number) => [`${val.toFixed(2)}%`, 'Policy Rate']}
          />
          <Bar dataKey="rate" radius={[0, 4, 4, 0]}>
            {rates.map((_, i) => (
              <Cell key={i} fill={barColors[i % barColors.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

// ── VIX Gauge ───────────────────────────────────────────────────────────────

function VixGauge({ vix }: { vix: { value: number | null; level: string; change?: number | null } }) {
  const val = vix.value ?? 0
  const levelColors: Record<string, string> = {
    LOW: 'text-emerald-400',
    MODERATE: 'text-amber-400',
    ELEVATED: 'text-orange-400',
    HIGH: 'text-rose-400',
    UNKNOWN: 'text-text-muted',
  }

  // Visual bar: 0–50 range
  const pctFill = Math.min(100, (val / 50) * 100)
  const barColor = val < 15 ? 'bg-emerald-500' : val < 20 ? 'bg-amber-500' : val < 30 ? 'bg-orange-500' : 'bg-rose-500'

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="panel-title">VIX Volatility Index</h3>
        <Activity size={16} className="text-text-muted" />
      </div>
      <div className="flex items-end gap-3 mb-3">
        <span className="text-3xl font-bold text-text-primary">{val.toFixed(1)}</span>
        <span className={clsx('text-sm font-medium', levelColors[vix.level] ?? 'text-text-muted')}>
          {vix.level}
        </span>
        {vix.change != null && (
          <span className={clsx('text-xs', vix.change >= 0 ? 'text-rose-400' : 'text-emerald-400')}>
            {(vix.change ?? 0) >= 0 ? '+' : ''}{(vix.change ?? 0).toFixed(2)}
          </span>
        )}
      </div>
      <div className="w-full h-3 bg-surface-DEFAULT rounded-full overflow-hidden">
        <div
          className={clsx('h-full rounded-full transition-all', barColor)}
          style={{ width: `${pctFill}%` }}
        />
      </div>
      <div className="flex justify-between text-xs text-text-muted mt-1">
        <span>0</span>
        <span>15</span>
        <span>25</span>
        <span>50</span>
      </div>
    </div>
  )
}

// ── Volatility Indices Panel ────────────────────────────────────────────────

function VolatilityIndicesPanel({ indices }: { indices: MacroVolatilityIndex[] }) {
  const levelColors: Record<string, string> = {
    LOW:      'text-emerald-400',
    MODERATE: 'text-amber-400',
    ELEVATED: 'text-orange-400',
    HIGH:     'text-rose-400',
    UNKNOWN:  'text-text-muted',
  }
  const barColors: Record<string, string> = {
    LOW:      'bg-emerald-500',
    MODERATE: 'bg-amber-500',
    ELEVATED: 'bg-orange-500',
    HIGH:     'bg-rose-500',
    UNKNOWN:  'bg-text-muted',
  }

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="panel-title">Volatility Indices</h3>
        <Activity size={16} className="text-text-muted" />
      </div>
      <div className="space-y-3">
        {indices.map(idx => {
          const val = idx.value ?? 0
          // Use 80 as scale ceiling for MOVE (it runs 50-200), 50 for VIX-family
          const ceiling = idx.key === 'MOVE' ? 200 : 50
          const pctFill = Math.min(100, (val / ceiling) * 100)
          return (
            <div key={idx.key}>
              <div className="flex items-center justify-between mb-1">
                <div>
                  <span className="text-xs font-bold text-text-secondary">{idx.name}</span>
                  <span className="ml-2 text-[10px] text-text-muted">{idx.description}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold font-mono text-text-primary">
                    {idx.value != null ? idx.value.toFixed(1) : '—'}
                  </span>
                  {idx.change != null && (
                    <span className={clsx('text-xs font-mono', idx.change >= 0 ? 'text-rose-400' : 'text-emerald-400')}>
                      {(idx.change ?? 0) >= 0 ? '+' : ''}{(idx.change ?? 0).toFixed(2)}
                    </span>
                  )}
                  <span className={clsx('text-[10px] font-medium', levelColors[idx.level] ?? 'text-text-muted')}>
                    {idx.level}
                  </span>
                </div>
              </div>
              <div className="w-full h-1.5 bg-surface-DEFAULT rounded-full overflow-hidden">
                <div
                  className={clsx('h-full rounded-full transition-all', barColors[idx.level] ?? 'bg-text-muted')}
                  style={{ width: `${pctFill}%` }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Commodity Grid ──────────────────────────────────────────────────────────

function CommodityGrid() {
  // Static commodity display (the dashboard endpoint could be extended)
  const commodities = [
    { name: 'Crude Oil (WTI)', ticker: 'CL=F', icon: '🛢️' },
    { name: 'Gold', ticker: 'GC=F', icon: '🥇' },
    { name: 'Silver', ticker: 'SI=F', icon: '🥈' },
    { name: 'Copper', ticker: 'HG=F', icon: '🔶' },
    { name: 'Natural Gas', ticker: 'NG=F', icon: '🔥' },
    { name: 'Wheat', ticker: 'ZW=F', icon: '🌾' },
  ]

  return (
    <div className="card p-4">
      <h3 className="panel-title mb-3">Commodities</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {commodities.map(c => (
          <div key={c.ticker} className="flex items-center gap-2 p-2 bg-surface-DEFAULT rounded-md">
            <span className="text-lg">{c.icon}</span>
            <div>
              <p className="text-xs font-medium text-text-primary">{c.name}</p>
              <p className="text-xs text-text-muted">{c.ticker}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Leading Indicators Traffic Lights ───────────────────────────────────────

function LeadingIndicatorsPanel({
  indicators,
  overallSignal,
}: {
  indicators: Array<{ name: string; value: number | null; signal: string; description: string; unit?: string }>
  overallSignal: string
}) {
  return (
    <div className="space-y-4">
      {/* Overall */}
      <div className="card p-4 flex items-center gap-4">
        <div className={clsx('w-6 h-6 rounded-full', SIGNAL_COLORS[overallSignal] ?? 'bg-text-muted')} />
        <div>
          <p className="panel-title">Overall Economic Signal</p>
          <p className={clsx('text-xs font-medium', SIGNAL_TEXT_COLORS[overallSignal] ?? 'text-text-muted')}>
            {(overallSignal ?? '').toUpperCase()}
          </p>
        </div>
      </div>

      {/* Individual indicators */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 stagger-children">
        {indicators.map(ind => (
          <div
            key={ind.name}
            className="card p-4 flex items-start gap-3"
          >
            <div className={clsx('w-4 h-4 rounded-full mt-0.5 shrink-0', SIGNAL_COLORS[ind.signal] ?? 'bg-text-muted')} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-text-primary">{ind.name}</p>
              {ind.value != null && (
                <p className="text-lg font-bold text-text-primary">
                  {(ind.value ?? 0).toFixed(2)}{ind.unit ? ` ${ind.unit}` : ''}
                </p>
              )}
              <p className="text-xs text-text-muted mt-1">{ind.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Inflation Tab ───────────────────────────────────────────────────────────

function InflationTab() {
  const { inflation, inflationLoading, inflationError, fetchInflation } = useMacro()

  useEffect(() => {
    if (!inflation) fetchInflation()
  }, [])

  if (inflationLoading) return <LoadingSkeleton />
  if (inflationError) return <ErrorPanel msg={inflationError} />
  if (!inflation) return null

  return (
    <div className="space-y-6">
      {/* CPI Table */}
      <div className="card overflow-x-auto">
        <table className="table-pro w-full text-sm">
          <thead>
            <tr>
              <th>Country</th>
              <th className="!text-right">CPI</th>
              <th className="!text-right">Target</th>
              <th className="!text-right">Deviation</th>
              <th className="!text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {inflation.inflation.map(row => (
              <tr key={row.country}>
                <td className="px-4 py-3 text-text-primary">
                  <span className="mr-2">{row.flag}</span>{row.name}
                </td>
                <td className="px-4 py-3 text-right font-mono text-text-primary">{(row.cpi ?? 0).toFixed(1)}%</td>
                <td className="px-4 py-3 text-right font-mono text-text-muted">{(row.target ?? 0).toFixed(1)}%</td>
                <td className={clsx(
                  'px-4 py-3 text-right font-mono',
                  row.deviation > 0 ? 'text-rose-400' : row.deviation < 0 ? 'text-emerald-400' : 'text-text-muted',
                )}>
                  {row.deviation > 0 ? '+' : ''}{(row.deviation ?? 0).toFixed(1)}%
                </td>
                <td className="px-4 py-3 text-center">
                  {row.above_target ? (
                    <TrendingUp size={14} className="inline text-rose-400" />
                  ) : (
                    <CheckCircle size={14} className="inline text-emerald-400" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* US CPI History */}
      {inflation.us_cpi_history.length > 0 && (
        <div className="card p-4">
          <h3 className="panel-title mb-3">US CPI Over Time</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={inflation.us_cpi_history}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey="date"
                tick={{ fill: '#9ca3af', fontSize: 11 }}
                tickFormatter={(d: string) => d.slice(5, 7) + '/' + d.slice(2, 4)}
              />
              <YAxis tick={{ fill: '#9ca3af', fontSize: 12 }} domain={['auto', 'auto']} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: 8, color: 'rgb(var(--text-primary))'}}
                labelStyle={{ color: '#e5e7eb' }}
              />
              <Line type="monotone" dataKey="value" stroke="#f59e0b" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}

// ── Rates Tab ───────────────────────────────────────────────────────────────

function RatesTab() {
  const { rates, ratesLoading, ratesError, fetchRates } = useMacro()

  useEffect(() => {
    if (!rates) fetchRates()
  }, [])

  if (ratesLoading) return <LoadingSkeleton />
  if (ratesError) return <ErrorPanel msg={ratesError} />
  if (!rates) return null

  return (
    <div className="space-y-6">
      <div className="card overflow-x-auto">
        <table className="table-pro w-full text-sm">
          <thead>
            <tr>
              <th>Country</th>
              <th>Central Bank</th>
              <th className="!text-right">Policy Rate</th>
              <th>Rate Name</th>
              <th className="!text-center">Currency</th>
            </tr>
          </thead>
          <tbody>
            {rates.rates.map(row => (
              <tr key={row.country}>
                <td className="px-4 py-3 text-text-primary">
                  <span className="mr-2">{row.flag}</span>{row.name}
                </td>
                <td className="px-4 py-3 text-text-secondary">{row.central_bank}</td>
                <td className="px-4 py-3 text-right font-mono font-bold text-text-primary">
                  {(row.policy_rate ?? 0).toFixed(2)}%
                </td>
                <td className="px-4 py-3 text-text-muted text-xs">{row.rate_name}</td>
                <td className="px-4 py-3 text-center text-text-secondary font-mono">{row.currency}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Bar chart */}
      <RatesBarChart rates={rates.rates.map(r => ({
        country: r.country,
        name: r.name,
        rate: r.policy_rate,
        flag: r.flag,
      }))} />
    </div>
  )
}

// ── Indicators Tab ──────────────────────────────────────────────────────────

function IndicatorsTab() {
  const { leading, leadingLoading, leadingError, fetchLeading } = useMacro()

  useEffect(() => {
    if (!leading) fetchLeading()
  }, [])

  if (leadingLoading) return <LoadingSkeleton />
  if (leadingError) return <ErrorPanel msg={leadingError} />
  if (!leading) return null

  return (
    <LeadingIndicatorsPanel
      indicators={leading.indicators}
      overallSignal={leading.overall_signal}
    />
  )
}

// ── Monetary Policy ──────────────────────────────────────────────────────────

const STANCE_CONFIG = {
  hawkish: { label: 'Hawkish', emoji: '🦅', color: 'text-rose-400', bg: 'bg-rose-500/10 border-rose-500/30' },
  dovish:  { label: 'Dovish',  emoji: '🕊️', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/30' },
  neutral: { label: 'Neutral', emoji: '⚖️', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/30' },
}

const EXPECTS_CONFIG = {
  cut:  { label: 'Cut',  color: 'text-emerald-400 bg-emerald-500/10' },
  hold: { label: 'Hold', color: 'text-amber-400 bg-amber-500/10' },
  hike: { label: 'Hike', color: 'text-rose-400 bg-rose-500/10' },
}

function StanceScoreBar({ score }: { score: number }) {
  // score: 1 (very dovish) to 10 (very hawkish)
  const pct = ((score - 1) / 9) * 100
  const color =
    score <= 3 ? 'bg-emerald-500'
    : score <= 5 ? 'bg-amber-400'
    : score <= 7 ? 'bg-orange-500'
    : 'bg-rose-500'

  return (
    <div>
      <div className="flex justify-between text-xs text-text-muted mb-1">
        <span>Dovish</span>
        <span className="font-mono font-semibold text-text-primary">{score}/10</span>
        <span>Hawkish</span>
      </div>
      <div className="w-full h-2 bg-surface-DEFAULT rounded-full overflow-hidden">
        <div
          className={clsx('h-full rounded-full transition-all', color)}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

function MarketProbBar({ prob }: { prob: { cut_25bps: number; hold: number; hike_25bps: number } }) {
  const segments = [
    { label: 'Cut', value: prob.cut_25bps, color: 'bg-emerald-500' },
    { label: 'Hold', value: prob.hold, color: 'bg-amber-500' },
    { label: 'Hike', value: prob.hike_25bps, color: 'bg-rose-500' },
  ]

  return (
    <div>
      <div className="w-full h-3 rounded-full overflow-hidden flex">
        {segments.map(s => (
          <div
            key={s.label}
            className={clsx('h-full', s.color)}
            style={{ width: `${s.value}%` }}
            title={`${s.label}: ${s.value}%`}
          />
        ))}
      </div>
      <div className="flex gap-3 mt-1">
        {segments.map(s => (
          <div key={s.label} className="flex items-center gap-1 text-xs text-text-muted">
            <div className={clsx('w-2 h-2 rounded-full', s.color)} />
            <span>{s.value}% {s.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function CentralBankCard({ bank }: { bank: CentralBankPolicy }) {
  const stance = STANCE_CONFIG[bank.stance] ?? STANCE_CONFIG.neutral
  const expects = EXPECTS_CONFIG[bank.market_expects] ?? EXPECTS_CONFIG.hold

  return (
    <div className={clsx(
      'card-hover p-5 flex flex-col gap-4',
      bank.stance === 'hawkish' ? 'border-rose-500/20'
      : bank.stance === 'dovish' ? 'border-emerald-500/20'
      : 'border-border',
    )}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-xl">{bank.flag}</span>
            <h3 className="text-sm font-bold text-text-primary leading-tight">{bank.bank}</h3>
          </div>
          <p className="text-xs text-text-muted">{bank.code} · {bank.currency}</p>
        </div>
        <div className={clsx('flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold', stance.bg, stance.color)}>
          <span>{stance.emoji}</span>
          <span>{stance.label}</span>
        </div>
      </div>

      {/* Rate */}
      <div className="flex items-end gap-3">
        <span className="text-3xl font-bold text-text-primary">{(bank.current_rate ?? 0).toFixed(2)}%</span>
        <div className="pb-1">
          <p className="text-xs text-text-muted">Last change</p>
          <p className="text-xs text-text-secondary">{bank.last_change}</p>
        </div>
      </div>

      {/* Stance score bar */}
      <StanceScoreBar score={bank.stance_score} />

      {/* Next meeting */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-text-secondary">
          <Calendar size={12} className="text-text-muted" />
          <span>Next meeting: <span className="text-text-primary font-medium">{bank.next_meeting}</span></span>
        </div>
        <span className="badge-blue font-mono">
          {bank.days_until}d
        </span>
      </div>

      {/* Market expects + probability bar */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-text-muted">Market expects</span>
            {bank.market_implied && (
              <span
                className={clsx(
                  'px-1.5 py-0.5 rounded text-[10px] font-semibold border',
                  bank.market_data_fresh
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                    : 'bg-amber-500/10 border-amber-500/30 text-amber-400',
                )}
                title={bank.market_data_fresh ? 'Live yield-curve data' : 'Static yield proxy'}
              >
                {bank.market_data_fresh ? 'Market-Implied' : 'Yield Proxy'}
              </span>
            )}
          </div>
          <span className={clsx('px-2 py-0.5 rounded-full text-xs font-semibold', expects.color)}>
            {expects.label}
          </span>
        </div>
        <MarketProbBar prob={bank.market_probability} />
        {/* Yield signal detail */}
        {bank.market_signal && (
          <p className="mt-2 text-[10px] text-text-muted leading-tight italic">
            {bank.market_signal}
          </p>
        )}
      </div>

      {/* Recent signals */}
      <div>
        <p className="text-xs font-semibold text-text-muted mb-2">Recent signals</p>
        <ul className="space-y-1">
          {bank.recent_signals.map((s, i) => (
            <li key={i} className="flex items-start gap-1.5 text-xs text-text-secondary">
              <span className="mt-0.5 shrink-0 text-text-muted">•</span>
              <span>{s}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Hawk / Dove factors */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-xs font-semibold text-rose-400 mb-1.5">Hawkish factors</p>
          <ul className="space-y-1">
            {bank.hawkish_factors.map((f, i) => (
              <li key={i} className="text-xs text-text-muted leading-tight">• {f}</li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-xs font-semibold text-emerald-400 mb-1.5">Dovish factors</p>
          <ul className="space-y-1">
            {bank.dovish_factors.map((f, i) => (
              <li key={i} className="text-xs text-text-muted leading-tight">• {f}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

function GlobalStanceMeter({ score, stance, phase }: { score: number; stance: string; phase: string }) {
  // Visual arc gauge — represented as a gradient bar for simplicity
  const pct = ((score - 1) / 9) * 100
  const stanceConfig = score >= 7 ? STANCE_CONFIG.hawkish
    : score <= 3 ? STANCE_CONFIG.dovish
    : STANCE_CONFIG.neutral

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="panel-title">Global Monetary Policy Stance</h3>
        <span className="text-xs px-2 py-0.5 rounded-full bg-surface-DEFAULT text-text-muted border border-border">
          avg {score}/10
        </span>
      </div>

      {/* Gradient bar */}
      <div className="relative w-full h-5 rounded-full overflow-hidden mb-3"
        style={{ background: 'linear-gradient(to right, #22c55e, #f59e0b, #ef4444)' }}>
        <div
          className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-surface-card border-2 border-text-primary/60 shadow"
          style={{ left: `calc(${pct}% - 8px)` }}
        />
      </div>
      <div className="flex justify-between text-xs text-text-muted mb-4">
        <span>1 Very Dovish</span>
        <span>5 Neutral</span>
        <span>10 Very Hawkish</span>
      </div>

      {/* Labels */}
      <div className="flex items-center gap-3">
        <div className={clsx(
          'flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-semibold',
          stanceConfig.bg, stanceConfig.color,
        )}>
          <span>{stanceConfig.emoji}</span>
          <span className="capitalize">{stance}</span>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-surface-DEFAULT border border-border">
          <Activity size={12} className="text-text-muted" />
          <span className="text-xs font-medium text-text-secondary capitalize">{phase}</span>
        </div>
      </div>
    </div>
  )
}

function UpcomingMeetingsTimeline({
  meetings,
}: {
  meetings: Array<{ bank: string; flag: string; date: string; days_until: number; market_expects: string }>
}) {
  return (
    <div className="card p-5">
      <h3 className="panel-title mb-4 flex items-center gap-2">
        <Calendar size={14} className="text-text-muted" />
        Upcoming Central Bank Meetings
      </h3>
      <div className="space-y-3">
        {meetings.map((m, i) => {
          const expectsConfig = EXPECTS_CONFIG[m.market_expects as keyof typeof EXPECTS_CONFIG] ?? EXPECTS_CONFIG.hold
          return (
            <div key={i} className="flex items-center gap-3">
              {/* Timeline dot */}
              <div className="flex flex-col items-center">
                <div className="w-2.5 h-2.5 rounded-full bg-blue" />
                {i < meetings.length - 1 && <div className="w-px h-7 bg-border mt-0.5" />}
              </div>
              {/* Content */}
              <div className="flex-1 flex items-center justify-between py-1">
                <div className="flex items-center gap-2">
                  <span className="text-base">{m.flag}</span>
                  <div>
                    <p className="text-sm font-medium text-text-primary">{m.bank}</p>
                    <p className="text-xs text-text-muted">{m.date}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={clsx('px-2 py-0.5 rounded-full text-xs font-semibold', expectsConfig.color)}>
                    {expectsConfig.label}
                  </span>
                  <span className="text-xs text-text-muted font-mono">{m.days_until}d</span>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Street Sentiment Panel ──────────────────────────────────────────────────

function SentimentScoreArc({ score }: { score: number | null }) {
  if (score === null) return <span className="text-text-muted text-sm">N/A</span>
  const color =
    score >= 62 ? 'text-emerald-400' :
    score >= 48 ? 'text-amber-400'   :
                  'text-rose-400'
  const bar =
    score >= 62 ? 'bg-emerald-500' :
    score >= 48 ? 'bg-amber-500'   :
                  'bg-rose-500'
  return (
    <div className="flex flex-col items-center gap-1">
      <span className={clsx('stat-hero tabular-nums', color)}>{score}</span>
      <div className="w-32 h-2 bg-surface-DEFAULT rounded-full overflow-hidden">
        <div className={clsx('h-full rounded-full transition-all', bar)} style={{ width: `${score}%` }} />
      </div>
      <span className="text-xs text-text-muted">/ 100</span>
    </div>
  )
}

/** Map a 0-100 score to the nearest Tailwind w-* step (multiples of 4 = 4% increments). */
function _scoreToWidthClass(score: number): string {
  // Clamp and round to nearest 4
  const clamped = Math.max(0, Math.min(100, score))
  const step = Math.round(clamped / 4) * 4
  const map: Record<number, string> = {
    0:'w-0', 4:'w-[4%]', 8:'w-[8%]', 12:'w-[12%]', 16:'w-[16%]', 20:'w-1/5',
    24:'w-[24%]', 28:'w-[28%]', 32:'w-[32%]', 36:'w-[36%]', 40:'w-2/5',
    44:'w-[44%]', 48:'w-[48%]', 52:'w-[52%]', 56:'w-[56%]', 60:'w-3/5',
    64:'w-[64%]', 68:'w-[68%]', 72:'w-[72%]', 76:'w-[76%]', 80:'w-4/5',
    84:'w-[84%]', 88:'w-[88%]', 92:'w-[92%]', 96:'w-[96%]', 100:'w-full',
  }
  return map[step] ?? 'w-1/2'
}

function ComponentRow({ label, value, scoreVal, weight, extra }: {
  label: string; value: string | null; scoreVal: number; weight: number; extra?: string
}) {
  const bar =
    scoreVal >= 62 ? 'bg-emerald-500' :
    scoreVal >= 48 ? 'bg-amber-500'   :
                     'bg-rose-500'
  const widthCls = _scoreToWidthClass(scoreVal)
  return (
    <div className="flex items-center gap-3">
      <div className="w-28 shrink-0">
        <p className="text-xs font-medium text-text-primary">{label}</p>
        <p className="text-[10px] text-text-muted">{weight}% weight</p>
      </div>
      <div className="flex-1">
        <div className="w-full h-1.5 bg-surface-DEFAULT rounded-full overflow-hidden">
          <div className={clsx('h-full rounded-full transition-all', bar, widthCls)} />
        </div>
      </div>
      <div className="w-28 text-right shrink-0">
        <p className="text-xs text-text-secondary">{value ?? 'N/A'}</p>
        {extra && <p className="text-[10px] text-text-muted">{extra}</p>}
      </div>
    </div>
  )
}

function StreetSentimentPanel({ sentiment }: { sentiment: StreetSentimentResponse }) {
  const c = sentiment.components
  const overallColor =
    sentiment.score !== null && sentiment.score >= 62 ? 'text-emerald-400' :
    sentiment.score !== null && sentiment.score >= 48 ? 'text-amber-400'   :
                                                        'text-rose-400'

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-5">
        <h3 className="panel-title flex items-center gap-2">
          <Activity size={14} className="text-text-muted" />
          Street Sentiment
        </h3>
        <span className={clsx('text-xs font-semibold capitalize px-2 py-0.5 rounded-full bg-surface-DEFAULT border border-border', overallColor)}>
          {sentiment.overall}
        </span>
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-6 mb-6">
        <SentimentScoreArc score={sentiment.score} />
        <p className="text-xs text-text-secondary leading-relaxed flex-1">{sentiment.interpretation}</p>
      </div>

      <div className="space-y-3">
        <ComponentRow
          label="Fear & Greed"
          value={c.fear_greed.label}
          scoreVal={c.fear_greed.score}
          weight={c.fear_greed.weight}
          extra={c.fear_greed.value !== null ? String(c.fear_greed.value) : undefined}
        />
        <ComponentRow
          label="VIX"
          value={c.vix.label}
          scoreVal={c.vix.score}
          weight={c.vix.weight}
          extra={c.vix.value !== null ? `${c.vix.value?.toFixed(1)}` : undefined}
        />
        <ComponentRow
          label="Put / Call"
          value={c.put_call.label}
          scoreVal={c.put_call.score}
          weight={c.put_call.weight}
          extra={c.put_call.value !== null ? `PCR ${c.put_call.value?.toFixed(2)}` : undefined}
        />
        <ComponentRow
          label="Breadth"
          value={c.breadth.label}
          scoreVal={c.breadth.score}
          weight={c.breadth.weight}
          extra={c.breadth.advancers !== undefined ? `${c.breadth.advancers}A / ${c.breadth.decliners}D` : undefined}
        />
        <ComponentRow
          label="Yields (10Y)"
          value={c.yields.label}
          scoreVal={c.yields.score}
          weight={c.yields.weight}
          extra={c.yields.direction}
        />
        <ComponentRow
          label="Dollar (DXY)"
          value={c.dollar.label}
          scoreVal={c.dollar.score}
          weight={c.dollar.weight}
          extra={c.dollar.direction}
        />
      </div>

      <p className="text-[10px] text-text-muted mt-4">Source: {sentiment.source}</p>
    </div>
  )
}

function MonetaryPolicyTab() {
  const { monetaryPolicy, monetaryPolicyLoading, monetaryPolicyError, fetchMonetaryPolicy } = useMacro()

  useEffect(() => {
    if (!monetaryPolicy) fetchMonetaryPolicy()
  }, [])

  if (monetaryPolicyLoading) return <LoadingSkeleton />
  if (monetaryPolicyError) return <ErrorPanel msg={monetaryPolicyError} />
  if (!monetaryPolicy) return null

  return (
    <div className="space-y-6">
      {/* Global stance + upcoming meetings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <GlobalStanceMeter
          score={monetaryPolicy.global_stance_score}
          stance={monetaryPolicy.global_stance}
          phase={monetaryPolicy.rate_cycle_phase}
        />
        <UpcomingMeetingsTimeline meetings={monetaryPolicy.upcoming_meetings} />
      </div>

      {/* Per-bank cards */}
      <div>
        <h2 className="panel-title mb-3">Central Bank Dashboard</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 stagger-children">
          {monetaryPolicy.central_banks.map(bank => (
            <CentralBankCard key={bank.code} bank={bank} />
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Economic Surprise Tab ───────────────────────────────────────────────────

const RESULT_CONFIG = {
  BEAT:   { label: 'Beat',   color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/30' },
  MISS:   { label: 'Miss',   color: 'text-rose-400',    bg: 'bg-rose-500/10 border-rose-500/30'    },
  INLINE: { label: 'Inline', color: 'text-text-muted',  bg: 'bg-surface-DEFAULT border-border'     },
}

function SurpriseIndexGauge({ value, trend }: { value: number; trend: string }) {
  // value: -100 to +100, bar centred at 0
  const pct = ((value + 100) / 200) * 100  // map -100..+100 → 0..100%
  const barColor =
    value >= 20  ? 'bg-emerald-500' :
    value <= -20 ? 'bg-rose-500'    :
                   'bg-amber-500'
  const trendColor =
    trend === 'bullish' ? 'text-emerald-400' :
    trend === 'bearish' ? 'text-rose-400'    :
                          'text-amber-400'

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="panel-title">Economic Surprise Index</h3>
        <span className={clsx('text-xs font-semibold capitalize px-2 py-0.5 rounded-full bg-surface-DEFAULT border border-border', trendColor)}>
          {trend}
        </span>
      </div>

      <div className="flex items-end gap-3 mb-4">
        <span className={clsx('text-4xl font-bold', value >= 20 ? 'text-emerald-400' : value <= -20 ? 'text-rose-400' : 'text-amber-400')}>
          {value > 0 ? '+' : ''}{value.toFixed(1)}
        </span>
        <span className="text-sm text-text-muted pb-1">/ 100</span>
      </div>

      {/* Track bar — centred at 0 */}
      <div className="relative w-full h-4 bg-surface-DEFAULT rounded-full overflow-hidden mb-1">
        {/* Centre line */}
        <div className="absolute left-1/2 top-0 w-px h-full bg-border z-10" />
        <div
          className={clsx('absolute top-0 h-full rounded-full transition-all', barColor)}
          style={{
            left: value >= 0 ? '50%' : `${pct}%`,
            width: `${Math.abs(value) / 2}%`,
          }}
        />
      </div>
      <div className="flex justify-between text-xs text-text-muted">
        <span>-100 Bearish</span>
        <span>0</span>
        <span>+100 Bullish</span>
      </div>
    </div>
  )
}

function SurpriseSummaryCards({ beats, misses, inline: inlineCount }: { beats: number; misses: number; inline: number }) {
  const total = beats + misses + inlineCount
  return (
    <div className="grid grid-cols-3 gap-3">
      {[
        { label: 'Beats', value: beats,       color: 'text-emerald-400', bg: 'border-emerald-500/20' },
        { label: 'Misses', value: misses,      color: 'text-rose-400',    bg: 'border-rose-500/20'    },
        { label: 'Inline', value: inlineCount, color: 'text-text-muted',  bg: 'border-border'         },
      ].map(s => (
        <div key={s.label} className={clsx('card p-4 text-center', s.bg)}>
          <p className={clsx('text-2xl font-bold', s.color)}>{s.value}</p>
          <p className="text-xs text-text-muted mt-1">{s.label}</p>
          <p className="text-xs text-text-muted">{total > 0 ? Math.round((s.value / total) * 100) : 0}%</p>
        </div>
      ))}
    </div>
  )
}

function SurpriseRunningChart({ data }: { data: Array<{ date: string; value: number }> }) {
  return (
    <div className="card p-4">
      <h3 className="panel-title mb-3">Running Surprise Index</h3>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis
            dataKey="date"
            tick={{ fill: '#9ca3af', fontSize: 11 }}
            tickFormatter={(d: string) => d.slice(5)}
          />
          <YAxis tick={{ fill: '#9ca3af', fontSize: 12 }} domain={[-100, 100]} />
          <Tooltip
            contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: 8, color: 'rgb(var(--text-primary))'}}
            labelStyle={{ color: '#e5e7eb' }}
            formatter={(v: number) => [v > 0 ? `+${v}` : `${v}`, 'Index']}
          />
          <ReferenceLine y={0} stroke="#6b7280" strokeDasharray="4 2" />
          <Line
            type="monotone"
            dataKey="value"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={{ fill: '#3b82f6', r: 3 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

function SurpriseReleasesTable({ releases }: { releases: EconomicRelease[] }) {
  return (
    <div className="card overflow-x-auto">
      <div className="px-4 py-3 border-b border-border bg-surface-DEFAULT">
        <h3 className="panel-title">Recent Economic Releases</h3>
      </div>
      <table className="table-pro w-full text-sm">
        <thead>
          <tr>
            <th>Date</th>
            <th>Indicator</th>
            <th className="!text-center">Cty</th>
            <th className="!text-right">Actual</th>
            <th className="!text-right">Consensus</th>
            <th className="!text-right">Diff</th>
            <th className="!text-center">Result</th>
          </tr>
        </thead>
        <tbody>
          {releases.map((r, i) => {
            const cfg = RESULT_CONFIG[r.result]
            return (
              <tr key={i}>
                <td className="px-4 py-2.5 text-xs text-text-muted font-mono">{r.date}</td>
                <td className="px-4 py-2.5">
                  <p className="text-xs font-medium text-text-primary">{r.indicator}</p>
                  <p className="text-xs text-text-muted leading-tight">{r.description}</p>
                </td>
                <td className="px-4 py-2.5 text-center text-xs text-text-muted font-mono">{r.country}</td>
                <td className="px-4 py-2.5 text-right text-xs font-mono font-semibold text-text-primary">
                  {r.actual}{r.unit === '%' || r.unit === 'idx' ? r.unit === 'idx' ? '' : '%' : ` ${r.unit}`}
                </td>
                <td className="px-4 py-2.5 text-right text-xs font-mono text-text-secondary">
                  {r.consensus}{r.unit === '%' ? '%' : r.unit === 'idx' ? '' : ` ${r.unit}`}
                </td>
                <td className={clsx(
                  'px-4 py-2.5 text-right text-xs font-mono font-semibold',
                  r.beat > 0 ? 'text-emerald-400' : r.beat < 0 ? 'text-rose-400' : 'text-text-muted',
                )}>
                  {r.diff > 0 ? '+' : ''}{r.diff}
                </td>
                <td className="px-4 py-2.5 text-center">
                  <span className={clsx('text-xs font-semibold px-2 py-0.5 rounded-full border', cfg.bg, cfg.color)}>
                    {cfg.label}
                  </span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function EconomicSurpriseTab() {
  const { economicSurprise, economicSurpriseLoading, economicSurpriseError, fetchEconomicSurprise } = useMacro()

  useEffect(() => {
    if (!economicSurprise) fetchEconomicSurprise()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (economicSurpriseLoading) return <LoadingSkeleton />
  if (economicSurpriseError) return <ErrorPanel msg={economicSurpriseError} />
  if (!economicSurprise) return null

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SurpriseIndexGauge value={economicSurprise.index_value} trend={economicSurprise.trend} />
        <SurpriseSummaryCards
          beats={economicSurprise.beats}
          misses={economicSurprise.misses}
          inline={economicSurprise.inline}
        />
      </div>
      <SurpriseRunningChart data={economicSurprise.running_index} />
      <SurpriseReleasesTable releases={economicSurprise.releases} />
    </div>
  )
}

// ── Global Rankings Tab ──────────────────────────────────────────────────────

const RANK_INDICATORS = [
  { key: 'gdp',          label: 'GDP Growth',       unit: '%' },
  { key: 'inflation',    label: 'CPI Inflation',     unit: '%' },
  { key: 'unemployment', label: 'Unemployment',      unit: '%' },
  { key: 'debt',         label: 'Govt Debt / GDP',   unit: '%' },
]

const RANK_BAR_COLORS: Record<string, string> = {
  green: '#22c55e',
  amber: '#f59e0b',
  red:   '#ef4444',
}

function RankingBarChart({ data }: { data: GlobalRankingResponse }) {
  // Show top 20 for readability
  const rows = data.countries.slice(0, 20)

  const chartData = rows.map(r => ({
    name: `${r.flag} ${r.country_code}`,
    value: r.latest_value ?? 0,
    color: r.color,
    fullName: r.name,
  }))

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="panel-title">{data.label} — Top 20</h3>
        <span className="text-xs text-text-muted">Source: World Bank</span>
      </div>
      <ResponsiveContainer width="100%" height={420}>
        <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 16 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis type="number" tick={{ fill: '#9ca3af', fontSize: 11 }} unit="%" />
          <YAxis
            dataKey="name"
            type="category"
            tick={{ fill: '#9ca3af', fontSize: 11 }}
            width={52}
          />
          <Tooltip
            contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: 8, color: 'rgb(var(--text-primary))'}}
            formatter={(val: number, _name: string, props) => [
              `${val.toFixed(1)}%`,
              (props.payload as { fullName: string }).fullName,
            ]}
          />
          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
            {chartData.map((entry, i) => (
              <Cell key={i} fill={RANK_BAR_COLORS[entry.color] ?? RANK_BAR_COLORS.amber} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

function RankingTable({ data }: { data: GlobalRankingResponse }) {
  const CELL_CLASSES: Record<string, string> = {
    green: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20',
    amber: 'bg-amber-500/15 text-amber-300 border-amber-500/20',
    red:   'bg-rose-500/15 text-rose-300 border-rose-500/20',
  }

  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-3 border-b border-border bg-surface-DEFAULT flex items-center justify-between">
        <h3 className="panel-title">{data.label} — All {data.count} Countries</h3>
        <span className="text-xs text-text-muted">{data.source}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="table-pro w-full text-sm">
          <thead>
            <tr>
              <th className="w-8">#</th>
              <th>Country</th>
              <th className="!text-right">{data.label}</th>
              <th className="!text-right">Year</th>
              <th>Region</th>
            </tr>
          </thead>
          <tbody>
            {data.countries.map(row => (
              <tr key={row.country_code}>
                <td className="px-4 py-2.5 text-xs text-text-muted font-mono">{row.rank}</td>
                <td className="px-4 py-2.5 text-text-primary">
                  <span className="mr-1.5">{row.flag}</span>
                  <span className="font-medium">{row.name}</span>
                  <span className="ml-2 text-xs text-text-muted font-mono">{row.country_code}</span>
                </td>
                <td className="px-4 py-2.5 text-right">
                  {row.latest_value !== null ? (
                    <span className={clsx(
                      'inline-block px-2 py-0.5 rounded-md border text-xs font-mono font-semibold',
                      CELL_CLASSES[row.color] ?? CELL_CLASSES.amber,
                    )}>
                      {(row.latest_value ?? 0).toFixed(1)}%
                    </span>
                  ) : (
                    <span className="text-xs text-text-muted">N/A</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right text-xs text-text-muted font-mono">
                  {row.latest_year ?? '—'}
                </td>
                <td className="px-4 py-2.5 text-xs text-text-muted">{row.region}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function G7SummaryCards({ g7 }: { g7: GroupSnapshotResponse }) {
  const agg = g7.aggregates
  const cards = [
    { label: 'Avg GDP Growth',   value: agg.avg_gdp_growth,   unit: '%', color: agg.avg_gdp_growth && agg.avg_gdp_growth >= 2 ? 'text-emerald-400' : 'text-amber-400' },
    { label: 'Avg Inflation',    value: agg.avg_inflation,    unit: '%', color: agg.avg_inflation && agg.avg_inflation <= 2.5 ? 'text-emerald-400' : agg.avg_inflation && agg.avg_inflation <= 4 ? 'text-amber-400' : 'text-rose-400' },
    { label: 'Avg Unemployment', value: agg.avg_unemployment, unit: '%', color: agg.avg_unemployment && agg.avg_unemployment < 5 ? 'text-emerald-400' : 'text-amber-400' },
  ]

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-text-primary">G7 Aggregates</h3>
        <span className="text-xs text-text-muted">Source: World Bank</span>
      </div>
      <div className="grid grid-cols-3 gap-4 mb-5">
        {cards.map(c => (
          <div key={c.label} className="text-center">
            <p className={clsx('text-2xl font-bold', c.color)}>
              {c.value !== null ? `${c.value?.toFixed(1)}${c.unit}` : '—'}
            </p>
            <p className="text-xs text-text-muted mt-1">{c.label}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {g7.members.map(m => (
          <div key={m.country_code} className="bg-surface-DEFAULT rounded-lg p-3 text-center">
            <div className="text-xl mb-1">{m.flag}</div>
            <p className="text-xs font-medium text-text-primary">{m.country_code}</p>
            <p className="text-xs text-text-muted mt-1">
              {m.gdp_growth?.latest_value !== null && m.gdp_growth?.latest_value !== undefined
                ? <span className={m.gdp_growth.latest_value >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
                    {(m.gdp_growth.latest_value ?? 0) >= 0 ? '+' : ''}{(m.gdp_growth.latest_value ?? 0).toFixed(1)}%
                  </span>
                : <span className="text-text-muted">—</span>
              }
            </p>
            <p className="text-xs text-text-muted/70">GDP</p>
          </div>
        ))}
      </div>
    </div>
  )
}

function GlobalRankingsTab() {
  const {
    globalRanking, globalRankingIndicator, globalRankingLoading, globalRankingError, fetchGlobalRanking,
    g7, g7Loading, g7Error, fetchG7,
  } = useMacro()

  const [activeIndicator, setActiveIndicator] = useState('gdp')

  useEffect(() => {
    fetchGlobalRanking(activeIndicator)
  }, [activeIndicator]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!g7) fetchG7()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-6">
      {/* Indicator selector */}
      <div className="glass rounded-xl p-2 flex items-center gap-2 flex-wrap">
        <span className="text-sm text-text-muted font-medium">Indicator:</span>
        {RANK_INDICATORS.map(ind => (
          <button
            key={ind.key}
            type="button"
            onClick={() => setActiveIndicator(ind.key)}
            className={clsx(
              'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border',
              activeIndicator === ind.key
                ? 'bg-blue/20 text-blue-light border-blue/30'
                : 'bg-surface-card text-text-secondary border-border hover:text-text-primary hover:bg-surface-DEFAULT',
            )}
          >
            {ind.label}
          </button>
        ))}
        <span className="ml-auto text-xs text-text-muted">Source: World Bank Open Data (free, no API key)</span>
      </div>

      {/* G7 cards */}
      {g7Loading && <div className="text-xs text-text-muted">Loading G7 data...</div>}
      {g7Error && <div className="text-xs text-rose-400">{g7Error}</div>}
      {g7 && !g7Loading && <G7SummaryCards g7={g7} />}

      {/* Rankings */}
      {globalRankingLoading && <LoadingSkeleton />}
      {globalRankingError && <ErrorPanel msg={globalRankingError} />}
      {globalRanking && !globalRankingLoading && (
        <div className="space-y-4">
          <RankingBarChart data={globalRanking} />
          <RankingTable data={globalRanking} />
        </div>
      )}
    </div>
  )
}

// ── Global Heatmap Tab ──────────────────────────────────────────────────────

const HEAT_CELL_CLASSES: Record<string, string> = {
  green: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20',
  amber: 'bg-amber-500/15 text-amber-300 border-amber-500/20',
  red:   'bg-rose-500/15 text-rose-300 border-rose-500/20',
}

function GlobalHeatmapTab() {
  const { heatMap, heatMapLoading, heatMapError, fetchHeatMap } = useMacro()

  useEffect(() => {
    if (!heatMap) fetchHeatMap()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (heatMapLoading) return <LoadingSkeleton />
  if (heatMapError) return <ErrorPanel msg={heatMapError} />
  if (!heatMap) return null

  const { rows, columns, column_labels, column_units, source } = heatMap

  return (
    <div className="space-y-4">
      {/* Legend + source badge */}
      <div className="flex items-center gap-4 text-xs text-text-muted flex-wrap">
        <span className="font-medium text-text-secondary">Key:</span>
        {[
          { color: 'green', label: 'Strong / Healthy' },
          { color: 'amber', label: 'Moderate' },
          { color: 'red',   label: 'Weak / At Risk'   },
        ].map(l => (
          <div key={l.color} className="flex items-center gap-1.5">
            <div className={clsx(
              'w-3 h-3 rounded-sm border',
              l.color === 'green' ? 'bg-emerald-500/30 border-emerald-500/40'
              : l.color === 'amber' ? 'bg-amber-500/30 border-amber-500/40'
              : 'bg-rose-500/30 border-rose-500/40',
            )} />
            <span>{l.label}</span>
          </div>
        ))}
        {source && (
          <span className="badge-blue ml-auto">
            {source}
          </span>
        )}
      </div>

      {/* Table */}
      <div className="card overflow-x-auto">
        <table className="table-pro w-full text-sm min-w-[700px]">
          <thead>
            <tr>
              <th className="sticky left-0 !bg-surface-DEFAULT z-10">
                Country
              </th>
              {columns.map(col => (
                <th key={col} className="!text-center whitespace-nowrap">
                  {column_labels[col]}
                  {column_units[col] && (
                    <span className="ml-1 text-text-muted/60">({column_units[col]})</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.country}>
                <td className="px-4 py-3 sticky left-0 bg-surface-card z-10">
                  <div className="flex items-center gap-2">
                    <span className="text-base">{row.flag}</span>
                    <div>
                      <p className="text-xs font-semibold text-text-primary">{row.name}</p>
                      <p className="text-xs text-text-muted">{row.country}</p>
                    </div>
                  </div>
                </td>
                {columns.map(col => {
                  const cell = row.cells[col]
                  if (!cell) return <td key={col} className="px-3 py-3 text-center text-text-muted text-xs">—</td>
                  return (
                    <td key={col} className="px-3 py-3 text-center">
                      <span className={clsx(
                        'inline-block px-2.5 py-1 rounded-md border text-xs font-mono font-semibold',
                        HEAT_CELL_CLASSES[cell.color] ?? HEAT_CELL_CLASSES.amber,
                      )}>
                        {(cell.value ?? 0).toFixed(1)}%
                      </span>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Per-indicator colour distribution bar */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {columns.map(col => {
          const counts = { green: 0, amber: 0, red: 0 }
          rows.forEach(row => {
            const c = row.cells[col]?.color
            if (c && c in counts) counts[c as keyof typeof counts]++
          })
          const total = rows.length
          return (
            <div key={col} className="card p-3">
              <p className="text-xs font-medium text-text-secondary mb-2">{column_labels[col]}</p>
              <div className="w-full h-2 rounded-full overflow-hidden flex">
                {[
                  { key: 'green', cls: 'bg-emerald-500' },
                  { key: 'amber', cls: 'bg-amber-500' },
                  { key: 'red',   cls: 'bg-rose-500'   },
                ].map(s => (
                  <div
                    key={s.key}
                    className={clsx('h-full', s.cls)}
                    style={{ width: `${(counts[s.key as keyof typeof counts] / total) * 100}%` }}
                    title={`${s.key}: ${counts[s.key as keyof typeof counts]}`}
                  />
                ))}
              </div>
              <div className="flex justify-between mt-1 text-xs text-text-muted">
                <span className="text-emerald-400">{counts.green}</span>
                <span className="text-amber-400">{counts.amber}</span>
                <span className="text-rose-400">{counts.red}</span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Shared components ───────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="skeleton h-24 w-full" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="skeleton h-32" />
        <div className="skeleton h-32" />
        <div className="skeleton h-32" />
      </div>
      <p className="text-text-muted text-sm text-center">Loading macro data...</p>
    </div>
  )
}

function ErrorPanel({ msg }: { msg: string }) {
  return (
    <div className="flex items-center gap-3 p-4 bg-rose/10 border border-rose/30 rounded-lg">
      <AlertTriangle size={16} className="text-rose-400 shrink-0" />
      <p className="text-sm text-rose-300">{msg}</p>
    </div>
  )
}

// ── Main Page ───────────────────────────────────────────────────────────────

export default function MacroPage() {
  const {
    dashboard, dashboardLoading, dashboardError, fetchDashboard,
    streetSentiment, streetSentimentLoading, fetchStreetSentiment,
    fetchRates, fetchInflation, fetchLeading, fetchMonetaryPolicy,
    fetchEconomicSurprise, fetchHeatMap,
    fetchGlobalRanking, globalRankingIndicator, fetchG7, fetchG20,
    fetchUKDashboard, fetchUKLeading, fetchUKRates,
    activeTab, setActiveTab,
  } = useMacro()

  const [searchParams] = useSearchParams()

  useEffect(() => {
    if (!dashboard) fetchDashboard()
    if (!streetSentiment) fetchStreetSentiment()
  }, [])

  // Deep-link support: /app/macro?tab=economic-index etc. Driven from
  // searchParams (not just mount) so in-app navigation switches tabs too.
  const tabParam = searchParams.get('tab')
  useEffect(() => {
    if (tabParam && (TAB_IDS as readonly string[]).includes(tabParam)) setActiveTab(tabParam)
  }, [tabParam, setActiveTab])

  // Refresh = dashboard + whatever the active tab shows. The economic-index
  // tab manages its own fetch/refresh cycle inside EconomicIndexTab.
  const refreshAll = () => {
    fetchDashboard()
    switch (activeTab) {
      case 'overview': fetchStreetSentiment(); break
      case 'rates': fetchRates(); break
      case 'inflation': fetchInflation(); break
      case 'indicators': fetchLeading(); break
      case 'monetary': fetchMonetaryPolicy(); break
      case 'surprise': fetchEconomicSurprise(); break
      case 'heatmap': fetchHeatMap(); break
      case 'rankings': fetchGlobalRanking(globalRankingIndicator); fetchG7(); fetchG20(); break
      case 'uk': fetchUKDashboard(); fetchUKLeading(); fetchUKRates(); break
    }
  }

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue/20 flex items-center justify-center">
            <Globe size={20} className="text-blue" />
          </div>
          <div>
            <h1 className="page-title">Macro Economics</h1>
            <p className="text-sm text-text-muted">Global economy overview</p>
          </div>
        </div>
        <button
          onClick={refreshAll}
          disabled={dashboardLoading}
          className="btn-secondary px-3 py-2 text-sm"
        >
          <RefreshCw size={14} className={dashboardLoading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <TabBar active={activeTab} onChange={setActiveTab} />

      {/* Tab content */}
      {activeTab === 'overview' && (
        <>
          {dashboardLoading && <LoadingSkeleton />}
          {dashboardError && <ErrorPanel msg={dashboardError} />}
          {dashboard && (
            <div className="space-y-6">
              {/* Street Sentiment panel */}
              {streetSentimentLoading && (
                <div className="skeleton h-32" />
              )}
              {streetSentiment && !streetSentiment.error && (
                <StreetSentimentPanel sentiment={streetSentiment} />
              )}
              {streetSentiment?.error && !streetSentimentLoading && (
                <div className="flex items-center gap-2 px-4 py-2 card text-xs text-text-muted">
                  <AlertTriangle size={12} className="text-amber-400 shrink-0" />
                  Street sentiment unavailable right now — the rest of the dashboard is unaffected.
                </div>
              )}

              {/* Smart-money positioning — CFTC TFF / Fed liquidity / TIC flows */}
              <PositioningCard />

              {/* Country grid */}
              <div>
                <h2 className="panel-title mb-3">
                  GDP Growth by Country
                </h2>
                <CountryGrid data={dashboard.gdp} />
              </div>

              {/* Row: Yield Curve + VIX + Recession */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <YieldCurveChart curves={dashboard.yield_curves} />
                <VixGauge vix={dashboard.vix} />
                <div className="card p-4">
                  <h3 className="panel-title mb-3">Recession Risk</h3>
                  {/* recession_signals can arrive as the UNKNOWN fallback when
                      the FRED fetch fails — never assume signals exists */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className={clsx(
                      'w-5 h-5 rounded-full',
                      dashboard.recession_signals?.recession_risk === 'HIGH' ? 'bg-rose-500'
                        : dashboard.recession_signals?.recession_risk === 'MODERATE' ? 'bg-amber-500'
                        : dashboard.recession_signals?.recession_risk === 'UNKNOWN' ? 'bg-text-muted'
                        : 'bg-emerald-500',
                    )} />
                    <span className="text-lg font-bold text-text-primary">
                      {dashboard.recession_signals?.recession_risk ?? 'UNKNOWN'}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {(dashboard.recession_signals?.signals ?? []).map((s, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <AlertTriangle size={12} className="text-amber-400 mt-0.5 shrink-0" />
                        <p className="text-xs text-text-muted">{s}</p>
                      </div>
                    ))}
                    {(dashboard.recession_signals?.signals ?? []).length === 0 && (
                      <div className="flex items-center gap-2">
                        {dashboard.recession_signals?.recession_risk === 'UNKNOWN' ? (
                          <>
                            <AlertTriangle size={12} className="text-amber-400" />
                            <p className="text-xs text-text-muted">Recession data temporarily unavailable</p>
                          </>
                        ) : (
                          <>
                            <CheckCircle size={12} className="text-emerald-400" />
                            <p className="text-xs text-text-muted">No recession signals detected</p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Volatility Indices */}
              {dashboard.volatility_indices && dashboard.volatility_indices.length > 0 && (
                <VolatilityIndicesPanel indices={dashboard.volatility_indices} />
              )}

              {/* Row: Rates bar chart + Commodities */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <RatesBarChart
                  rates={dashboard.central_bank_rates.map(r => ({
                    country: r.country,
                    name: r.name,
                    rate: r.rate,
                    flag: r.flag,
                  }))}
                />
                <CommodityGrid />
              </div>

              {/* Inflation & Unemployment summary */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Inflation */}
                <div className="card p-4">
                  <h3 className="panel-title mb-3 flex items-center gap-2">
                    <TrendingUp size={14} />
                    Inflation (CPI)
                  </h3>
                  <div className="space-y-2">
                    {dashboard.inflation.map(c => (
                      <div key={c.country} className="flex items-center justify-between">
                        <span className="text-xs text-text-secondary">
                          {c.flag} {c.name}
                        </span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-surface-DEFAULT rounded-full overflow-hidden">
                            <div
                              className={clsx(
                                'h-full rounded-full',
                                c.cpi > 3 ? 'bg-rose-500' : c.cpi > 2 ? 'bg-amber-500' : 'bg-emerald-500',
                              )}
                              style={{ width: `${Math.min(100, (c.cpi / 6) * 100)}%` }}
                            />
                          </div>
                          <span className="text-xs font-mono text-text-primary w-10 text-right">
                            {(c.cpi ?? 0).toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Unemployment */}
                <div className="card p-4">
                  <h3 className="panel-title mb-3 flex items-center gap-2">
                    <TrendingDown size={14} />
                    Unemployment
                  </h3>
                  <div className="space-y-2">
                    {dashboard.unemployment.map(c => (
                      <div key={c.country} className="flex items-center justify-between">
                        <span className="text-xs text-text-secondary">
                          {c.flag} {c.name}
                        </span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-surface-DEFAULT rounded-full overflow-hidden">
                            <div
                              className={clsx(
                                'h-full rounded-full',
                                c.rate > 6 ? 'bg-rose-500' : c.rate > 4 ? 'bg-amber-500' : 'bg-emerald-500',
                              )}
                              style={{ width: `${Math.min(100, (c.rate / 10) * 100)}%` }}
                            />
                          </div>
                          <span className="text-xs font-mono text-text-primary w-10 text-right">
                            {(c.rate ?? 0).toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* FOMC Calendar */}
              {dashboard.fomc_calendar.length > 0 && (
                <div className="card p-4">
                  <h3 className="panel-title mb-3 flex items-center gap-2">
                    <DollarSign size={14} />
                    Upcoming FOMC Meetings
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {dashboard.fomc_calendar.slice(0, 4).map(m => (
                      <div
                        key={m.start_date}
                        className="px-4 py-3 bg-surface-DEFAULT rounded-lg border border-border/50"
                      >
                        <p className="text-sm font-medium text-text-primary">{m.start_date}</p>
                        <p className="text-xs text-text-muted">in {m.days_until} days</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {activeTab === 'rates' && <RatesTab />}
      {activeTab === 'inflation' && <InflationTab />}
      {activeTab === 'indicators' && <IndicatorsTab />}
      {activeTab === 'monetary' && <MonetaryPolicyTab />}
      {activeTab === 'surprise' && <EconomicSurpriseTab />}
      {activeTab === 'heatmap' && <GlobalHeatmapTab />}
      {activeTab === 'rankings' && <GlobalRankingsTab />}
      {activeTab === 'economic-index' && (
        <Suspense fallback={<div className="p-8 text-text-muted">Loading economic index...</div>}>
          <EconomicIndexTab />
        </Suspense>
      )}
      {activeTab === 'uk' && <UKEconomyTab />}
    </div>
  )
}

// ── UK Economy Tab ───────────────────────────────────────────────────────────

function UKStatCard({ label, value, unit, sub, color }: {
  label: string
  value: string | number | null
  unit?: string
  sub?: string
  color?: string
}) {
  return (
    <div className="card p-4">
      <p className="text-xs text-text-muted mb-1">{label}</p>
      <p className={clsx('text-2xl font-bold', color ?? 'text-text-primary')}>
        {value != null ? `${value}${unit ?? ''}` : '—'}
      </p>
      {sub && <p className="text-xs text-text-muted mt-1">{sub}</p>}
    </div>
  )
}

function UKHistoryChart({
  title,
  data,
  color,
  unit,
  referenceLine,
  referenceLabel,
}: {
  title: string
  data: Array<{ date: string; value: number }>
  color: string
  unit?: string
  referenceLine?: number
  referenceLabel?: string
}) {
  const chartData = [...data].reverse()
  return (
    <div className="card p-4">
      <h3 className="panel-title mb-3">{title}</h3>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis
            dataKey="date"
            tick={{ fill: '#9ca3af', fontSize: 11 }}
            tickFormatter={(d: string) => d.slice(0, 7)}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fill: '#9ca3af', fontSize: 12 }}
            domain={['auto', 'auto']}
            unit={unit}
          />
          <Tooltip
            contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: 8, color: 'rgb(var(--text-primary))'}}
            labelStyle={{ color: '#e5e7eb' }}
            formatter={(v: number) => [`${v.toFixed(2)}${unit ?? ''}`, title]}
          />
          {referenceLine != null && (
            <ReferenceLine
              y={referenceLine}
              stroke="#6b7280"
              strokeDasharray="4 4"
              label={{ value: referenceLabel ?? `${referenceLine}`, fill: '#9ca3af', fontSize: 11 }}
            />
          )}
          <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

function UKGiltCurveChart({ yields }: { yields: UKRatesResponse['gilt_yields']['yields'] }) {
  const tenors = ['2Y', '5Y', '10Y', '30Y']
  const chartData = tenors.map(t => ({
    tenor: t,
    yield: yields[t]?.latest_value ?? null,
  }))

  return (
    <div className="card p-4">
      <h3 className="panel-title mb-1">UK Gilt Yield Curve</h3>
      <p className="text-xs text-text-muted mb-3">BoE zero-coupon nominal curve</p>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis dataKey="tenor" tick={{ fill: '#9ca3af', fontSize: 12 }} />
          <YAxis tick={{ fill: '#9ca3af', fontSize: 12 }} domain={['auto', 'auto']} unit="%" />
          <Tooltip
            contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: 8, color: 'rgb(var(--text-primary))'}}
            formatter={(v: number) => [`${v?.toFixed(2)}%`, 'Yield']}
          />
          <Line
            type="monotone"
            dataKey="yield"
            stroke="#f59e0b"
            strokeWidth={2}
            dot={{ fill: '#f59e0b', r: 5 }}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
      {/* Current values */}
      <div className="grid grid-cols-4 gap-2 mt-3">
        {tenors.map(t => (
          <div key={t} className="text-center">
            <p className="text-xs text-text-muted">{t}</p>
            <p className="text-sm font-mono font-semibold text-amber-400">
              {yields[t]?.latest_value != null ? `${yields[t].latest_value!.toFixed(2)}%` : '—'}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}

function UKLeadingIndicatorsGrid({ data }: { data: UKLeadingIndicatorsResponse }) {
  const SIGNAL_DOT: Record<string, string> = {
    green: 'bg-emerald-500',
    amber: 'bg-amber-500',
    red:   'bg-rose-500',
  }
  const SIGNAL_TEXT: Record<string, string> = {
    green: 'text-emerald-400',
    amber: 'text-amber-400',
    red:   'text-rose-400',
  }

  return (
    <div className="space-y-4">
      <div className="card p-4 flex items-center gap-4">
        <div className={clsx('w-6 h-6 rounded-full', SIGNAL_DOT[data.overall_signal] ?? 'bg-text-muted')} />
        <div>
          <p className="panel-title">UK Overall Economic Signal</p>
          <p className={clsx('text-xs font-medium', SIGNAL_TEXT[data.overall_signal] ?? 'text-text-muted')}>
            {(data.overall_signal ?? '').toUpperCase()} — risk score: {data.risk_score}
          </p>
        </div>
        <div className="ml-auto flex gap-3 text-xs">
          <span className="text-emerald-400">{data.signal_counts.green ?? 0} green</span>
          <span className="text-amber-400">{data.signal_counts.amber ?? 0} amber</span>
          <span className="text-rose-400">{data.signal_counts.red ?? 0} red</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {data.indicators.map(ind => (
          <div
            key={ind.name}
            className="card p-4 flex items-start gap-3"
          >
            <div className={clsx('w-4 h-4 rounded-full mt-0.5 shrink-0', SIGNAL_DOT[ind.signal] ?? 'bg-text-muted')} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-text-primary">{ind.name}</p>
              {ind.value != null && (
                <p className="text-lg font-bold text-text-primary">
                  {(ind.value ?? 0).toFixed(2)}{ind.unit ? ` ${ind.unit}` : ''}
                </p>
              )}
              <p className="text-xs text-text-muted mt-1">{ind.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function UKEconomyTab() {
  const {
    ukDashboard, ukDashboardLoading, ukDashboardError, fetchUKDashboard,
    ukLeading, ukLeadingLoading, ukLeadingError, fetchUKLeading,
    ukRates, ukRatesLoading, ukRatesError, fetchUKRates,
  } = useMacro()

  useEffect(() => {
    if (!ukDashboard) fetchUKDashboard()
    if (!ukLeading) fetchUKLeading()
    if (!ukRates) fetchUKRates()
  }, [])

  const loading = ukDashboardLoading || ukLeadingLoading || ukRatesLoading
  const error = ukDashboardError || ukLeadingError || ukRatesError

  if (loading && !ukDashboard) return <LoadingSkeleton />
  if (error && !ukDashboard) return <ErrorPanel msg={error} />

  const dash = ukDashboard
  const cpiVal = dash?.cpi?.latest_value
  const unempVal = dash?.unemployment?.latest_value
  const brVal = dash?.bank_rate?.latest_value
  const hpVal = dash?.house_prices?.latest_value
  const rsVal = dash?.retail_sales?.latest_value

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-3xl">🇬🇧</span>
          <div>
            <h2 className="text-lg font-bold text-text-primary">United Kingdom Economy</h2>
            <p className="text-xs text-text-muted">Live data from ONS & Bank of England APIs</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => { fetchUKDashboard(); fetchUKLeading(); fetchUKRates() }}
          className="btn-secondary px-3 py-1.5 text-xs"
        >
          <RefreshCw size={12} />
          Refresh
        </button>
      </div>

      {/* Key stats grid */}
      {dash && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 stagger-children">
          <UKStatCard
            label="CPI Inflation"
            value={cpiVal?.toFixed(1) ?? null}
            unit="%"
            sub={`Target: 2.0% ${cpiVal != null ? (cpiVal > 2 ? '▲ above' : '✓ on target') : ''}`}
            color={cpiVal != null ? (cpiVal > 4 ? 'text-rose-400' : cpiVal > 2 ? 'text-amber-400' : 'text-emerald-400') : undefined}
          />
          <UKStatCard
            label="Unemployment"
            value={unempVal?.toFixed(1) ?? null}
            unit="%"
            sub="Aged 16+, SA"
            color={unempVal != null ? (unempVal > 6 ? 'text-rose-400' : unempVal > 4.5 ? 'text-amber-400' : 'text-emerald-400') : undefined}
          />
          <UKStatCard
            label="BoE Bank Rate"
            value={brVal?.toFixed(2) ?? null}
            unit="%"
            sub="Neutral est: 2.5%"
            color="text-blue-400"
          />
          <UKStatCard
            label="House Price Index"
            value={hpVal?.toFixed(0) ?? null}
            sub={dash.house_prices?.latest_date ?? undefined}
          />
          <UKStatCard
            label="Retail Sales YoY"
            value={rsVal?.toFixed(1) ?? null}
            unit="%"
            color={rsVal != null ? (rsVal > 0 ? 'text-emerald-400' : 'text-rose-400') : undefined}
          />
        </div>
      )}

      {/* GDP + CPI charts */}
      {dash && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {(dash.gdp?.history?.length ?? 0) > 0 && (
            <UKHistoryChart
              title="UK GDP (Quarterly, £bn chained)"
              data={dash.gdp.history}
              color="#3b82f6"
            />
          )}
          {(dash.cpi?.history?.length ?? 0) > 0 && (
            <UKHistoryChart
              title="UK CPI Inflation (Annual Rate)"
              data={dash.cpi.history}
              color="#f59e0b"
              unit="%"
              referenceLine={2}
              referenceLabel="2% target"
            />
          )}
        </div>
      )}

      {/* Unemployment + House Prices */}
      {dash && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {(dash.unemployment?.history?.length ?? 0) > 0 && (
            <UKHistoryChart
              title="UK Unemployment Rate"
              data={dash.unemployment.history}
              color="#22c55e"
              unit="%"
            />
          )}
          {(dash.house_prices?.history?.length ?? 0) > 0 && (
            <UKHistoryChart
              title="UK House Price Index"
              data={dash.house_prices.history}
              color="#8b5cf6"
            />
          )}
        </div>
      )}

      {/* Bank Rate history */}
      {dash && (dash.bank_rate?.history?.length ?? 0) > 0 && (
        <UKHistoryChart
          title="BoE Bank Rate History"
          data={dash.bank_rate.history}
          color="#ef4444"
          unit="%"
          referenceLine={2.5}
          referenceLabel="neutral"
        />
      )}

      {/* Gilt yield curve */}
      {ukRates && ukRates.gilt_yields?.yields && (
        <UKGiltCurveChart yields={ukRates.gilt_yields.yields} />
      )}

      {/* Leading indicators */}
      {ukLeadingLoading && <LoadingSkeleton />}
      {ukLeadingError && <ErrorPanel msg={ukLeadingError} />}
      {ukLeading && (
        <div>
          <h3 className="panel-title mb-3">UK Leading Indicators</h3>
          <UKLeadingIndicatorsGrid data={ukLeading} />
        </div>
      )}

      {/* Source attribution */}
      <p className="text-xs text-text-muted text-right">
        Sources: Office for National Statistics (ONS) · Bank of England Statistical Interactive Database · Cached 1–6h
      </p>
    </div>
  )
}
